interface JsonLdProps {
  data: Record<string, unknown>;
}

/**
 * Sanitizes a value for safe inclusion in JSON-LD structured data.
 * Removes potentially dangerous content like script tags and event handlers.
 * 
 * SECURITY NOTE: This function should be applied to any user-generated content
 * before including it in schema generation functions.
 */
const sanitizeJsonLdValue = (value: unknown): unknown => {
  if (typeof value === 'string') {
    // Remove script tags and event handlers from strings
    return value
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/on\w+\s*=/gi, '')
      .replace(/<[^>]*>/g, '') // Strip all HTML tags
      .trim();
  }
  if (Array.isArray(value)) {
    return value.map(sanitizeJsonLdValue);
  }
  if (value !== null && typeof value === 'object') {
    const sanitized: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      sanitized[key] = sanitizeJsonLdValue(val);
    }
    return sanitized;
  }
  return value;
};

/**
 * JsonLd Component for rendering structured data.
 * 
 * SECURITY: All data is sanitized before rendering to prevent XSS attacks.
 * JSON.stringify() handles escaping for JSON context, and additional sanitization
 * removes any potentially malicious HTML/script content from string values.
 */
const JsonLd = ({ data }: JsonLdProps) => {
  // Sanitize all data before stringifying to prevent XSS
  const sanitizedData = sanitizeJsonLdValue(data) as Record<string, unknown>;
  
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(sanitizedData) }}
    />
  );
};

// IMPORTANT: LocalBusiness schema is defined ONLY in index.html to prevent duplicates.
// Reference the @id "https://ifixappleservicecenter.in/#localbusiness" when linking from Service schemas.

// FAQ Schema Generator
export const generateFaqSchema = (faqs: { question: string; answer: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map((faq) => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

// Article Schema Generator with proper image support
export const generateArticleSchema = ({
  title,
  description,
  datePublished,
  dateModified,
  url,
  image,
}: {
  title: string;
  description: string;
  datePublished: string;
  dateModified?: string;
  url: string;
  image?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": title,
  "description": description,
  "datePublished": datePublished,
  "dateModified": dateModified || datePublished,
  "url": url,
  "image": {
    "@type": "ImageObject",
    "url": image || "https://ifixappleservicecenter.in/og-image.jpg",
    "width": 1200,
    "height": 630
  },
  "author": {
    "@type": "Organization",
    "name": "iFix Apple Care",
    "url": "https://ifixappleservicecenter.in"
  },
  "publisher": {
    "@type": "Organization",
    "name": "iFix Apple Care",
    "logo": {
      "@type": "ImageObject",
      "url": "https://ifixappleservicecenter.in/logo.png"
    }
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": url
  }
});

// Service Schema Generator with AggregateRating - references LocalBusiness via @id
export const generateServiceSchema = ({
  name,
  description,
  url,
  ratingValue = 4.9,
  reviewCount = 500,
}: {
  name: string;
  description: string;
  url: string;
  ratingValue?: number;
  reviewCount?: number;
}) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": name,
  "description": description,
  "url": url,
  "provider": {
    "@id": "https://ifixappleservicecenter.in/#localbusiness"
  },
  "areaServed": {
    "@type": "State",
    "name": "Bihar"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": ratingValue,
    "reviewCount": reviewCount,
    "bestRating": 5,
    "worstRating": 1
  }
});

// Product Schema Generator for specific repair services
export const generateProductSchema = ({
  name,
  description,
  url,
  image,
  priceRange,
  ratingValue = 4.9,
  reviewCount = 150,
}: {
  name: string;
  description: string;
  url: string;
  image?: string;
  priceRange: string;
  ratingValue?: number;
  reviewCount?: number;
}) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  "name": name,
  "description": description,
  "url": url,
  "image": image || "https://ifixappleservicecenter.in/og-image.jpg",
  "brand": {
    "@type": "Brand",
    "name": "iFix Apple Care"
  },
  "offers": {
    "@type": "AggregateOffer",
    "priceCurrency": "INR",
    "priceSpecification": {
      "@type": "PriceSpecification",
      "price": priceRange
    },
    "availability": "https://schema.org/InStock",
    "seller": {
      "@id": "https://ifixappleservicecenter.in/#localbusiness"
    }
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": ratingValue,
    "reviewCount": reviewCount,
    "bestRating": 5,
    "worstRating": 1
  }
});

// Breadcrumb Schema Generator
export const generateBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

// WebSite Schema with SearchAction for sitelinks search box
export const generateWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "iFix Apple Care",
  "url": "https://ifixappleservicecenter.in",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": "https://ifixappleservicecenter.in/blog?search={search_term_string}"
    },
    "query-input": "required name=search_term_string"
  }
});

// Organization Schema with aggregateRating
export const generateOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "iFix Apple Care",
  "url": "https://ifixappleservicecenter.in",
  "logo": "https://ifixappleservicecenter.in/logo.png",
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+91-9798653591",
    "contactType": "customer service",
    "areaServed": "IN",
    "availableLanguage": ["English", "Hindi"]
  },
  "sameAs": [
    "https://www.facebook.com/iFixapplepatna/",
    "https://www.instagram.com/ifixapplepatna/",
    "https://www.youtube.com/@ifixapplepatna111"
  ]
});

export default JsonLd;
